import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Box, Button, Dialog, TextField, Typography, IconButton, CircularProgress,
  Chip, InputAdornment, Collapse, Autocomplete,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { useFormik } from "formik";
import { useCreateVendorMutation, useGetVendorQuery } from "../../../Api/attoDeskApi";
import { useNotifier } from "../../../Core/Notifier";
import { IVendor } from "../../../Api/Interface/api.interface";
import { uploadImage } from "../../../Components/UploadFile/upload";

interface AddressSuggestion {
  display_name: string;
  address: {
    house_number?: string;
    road?: string;
    city?: string;
    town?: string;
    village?: string;
    state?: string;
    province?: string;
    postcode?: string;
    country?: string;
  };
}

interface Props {
  openModel?: boolean;
  handleCloseDialog: (close: boolean) => void;
  data?: IVendor;
  onSuccess?: (newSupplier: any) => void;
}

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 1.5 },
  },
};

const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

const QuickAddSupplier: React.FC<Props> = ({ openModel = false, handleCloseDialog, onSuccess }) => {
  const [createVendor, { isLoading }] = useCreateVendorMutation();
  const { showErrorMessage, showMessage } = useNotifier();
  const [selectedImage, setSelectedImage] = useState<File | undefined>(undefined);
  const [duplicateWarning, setDuplicateWarning] = useState("");
  const [addressOptions, setAddressOptions] = useState<AddressSuggestion[]>([]);
  const [addressInput, setAddressInput] = useState("");
  const [addressLoading, setAddressLoading] = useState(false);

  // Fetch existing suppliers for duplicate check
  const { data: vendorData } = useGetVendorQuery({ searchText: "", id: "" });
  const existingSuppliers = useMemo(() => {
    return (vendorData?.data as IVendor[] || []).filter((v: any) => !v.isDelete);
  }, [vendorData]);

  // Address autofill via Nominatim
  useEffect(() => {
    if (addressInput.length < 3) { setAddressOptions([]); return; }
    const timer = setTimeout(async () => {
      setAddressLoading(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=ca&limit=5&q=${encodeURIComponent(addressInput)}`,
          { headers: { "Accept-Language": "en" } }
        );
        const data = await res.json();
        setAddressOptions(data || []);
      } catch { setAddressOptions([]); }
      finally { setAddressLoading(false); }
    }, 400);
    return () => clearTimeout(timer);
  }, [addressInput]);

  const formik = useFormik({
    initialValues: {
      customerCompanyName: "",
      customerCompanyPhoneNumebr: "",
      customerEmail: "",
      customerCity: "",
      customerStreetNumber: "",
      customerStreetName: "",
      customerProvince: "",
      customerPostalCode: "",
    },
    validate: (values) => {
      const errors: Record<string, string> = {};
      if (!values.customerCompanyName.trim()) errors.customerCompanyName = "Supplier name is required";
      if (!values.customerCompanyPhoneNumebr.trim()) {
        errors.customerCompanyPhoneNumebr = "Phone number is required";
      } else {
        const digits = values.customerCompanyPhoneNumebr.replace(/\D/g, "");
        if (digits.length < 10) errors.customerCompanyPhoneNumebr = "Phone must be at least 10 digits";
        if (digits.length > 15) errors.customerCompanyPhoneNumebr = "Phone number is too long";
      }
      if (values.customerEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.customerEmail)) errors.customerEmail = "Invalid email";
      return errors;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        let customerImg: string | undefined;
        if (selectedImage) customerImg = (await uploadImage(selectedImage)) || "";

        const payload = {
          customerCompanyName: values.customerCompanyName,
          customerCompanyPhoneNumebr: values.customerCompanyPhoneNumebr,
          customerEmail: values.customerEmail,
          customerCity: values.customerCity,
          customerStreetNumber: values.customerStreetNumber,
          customerStreetName: values.customerStreetName,
          customerProvince: values.customerProvince,
          customerPostalCode: values.customerPostalCode,
          customerImg,
        };

        const res = await createVendor(payload).unwrap();
        if (!res.status) {
          showErrorMessage(res.message);
        } else {
          showMessage("Supplier created");
          if (onSuccess && res.data) onSuccess(res.data);
          resetForm();
          setSelectedImage(undefined);
          setAddressInput("");
          handleCloseDialog(false);
        }
      } catch {
        showErrorMessage("Something went wrong");
      }
    },
  });

  // Duplicate check on name change
  useEffect(() => {
    const name = formik.values.customerCompanyName.trim().toLowerCase();
    if (name.length < 2) { setDuplicateWarning(""); return; }
    const match = existingSuppliers.find(s =>
      s.customerCompanyName?.toLowerCase() === name
    );
    setDuplicateWarning(match ? `"${match.customerCompanyName}" already exists` : "");
  }, [formik.values.customerCompanyName, existingSuppliers]);

  const phoneDigits = formik.values.customerCompanyPhoneNumebr.replace(/\D/g, "");
  const formValid = formik.values.customerCompanyName.trim() !== "" && phoneDigits.length >= 10 && phoneDigits.length <= 15;

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/jpg", "image/gif"].includes(file.type)) { showErrorMessage("Must be an image"); return; }
    if (file.size > 2048 * 1024) { showErrorMessage("Must be under 2 MB"); return; }
    setSelectedImage(file);
  };

  return (
    <Dialog open={openModel} onClose={() => handleCloseDialog(false)} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>

      {/* Header */}
      <Box sx={{ background: "linear-gradient(135deg, #0f172a, #1e293b)", px: 3, py: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <StorefrontIcon sx={{ fontSize: 20, color: "#fff" }} />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography fontSize={16} fontWeight={700} color="#fff">Quick Add Supplier</Typography>
          <Typography fontSize={11} color="rgba(255,255,255,0.5)">Create and continue your workflow</Typography>
        </Box>
        <IconButton onClick={() => handleCloseDialog(false)} size="small" sx={{ color: "rgba(255,255,255,0.5)", "&:hover": { color: "#fff" } }}>
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>

      <form onSubmit={formik.handleSubmit}>
        <Box sx={{ p: 3 }}>
          {/* Company Name — required */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
              Supplier Name <Typography component="span" color="#ef4444" fontSize={11}>*</Typography>
            </Typography>
            <TextField fullWidth size="small" placeholder="Enter supplier name"
              {...formik.getFieldProps("customerCompanyName")}
              error={formik.touched.customerCompanyName && Boolean(formik.errors.customerCompanyName)}
              helperText={formik.touched.customerCompanyName && formik.errors.customerCompanyName}
              sx={inputSx} />
            <Collapse in={!!duplicateWarning}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.75, px: 1 }}>
                <WarningAmberIcon sx={{ fontSize: 14, color: "#f59e0b" }} />
                <Typography fontSize={11} color="#f59e0b" fontWeight={600}>{duplicateWarning}</Typography>
              </Box>
            </Collapse>
          </Box>

          {/* Phone — required with formatting & validation */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75, display: "flex", alignItems: "center", gap: 0.5 }}>
              Phone Number <Typography component="span" color="#ef4444" fontSize={11}>*</Typography>
            </Typography>
            <TextField fullWidth size="small" placeholder="(555) 000-0000"
              value={formik.values.customerCompanyPhoneNumebr}
              onChange={e => formik.setFieldValue("customerCompanyPhoneNumebr", formatPhone(e.target.value))}
              onBlur={formik.handleBlur}
              name="customerCompanyPhoneNumebr"
              error={formik.touched.customerCompanyPhoneNumebr && Boolean(formik.errors.customerCompanyPhoneNumebr)}
              helperText={formik.touched.customerCompanyPhoneNumebr && formik.errors.customerCompanyPhoneNumebr}
              sx={inputSx} />
          </Box>

          {/* Email */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75 }}>Email</Typography>
            <TextField fullWidth size="small" placeholder="email@example.com" type="email"
              {...formik.getFieldProps("customerEmail")}
              error={formik.touched.customerEmail && Boolean(formik.errors.customerEmail)}
              helperText={formik.touched.customerEmail && formik.errors.customerEmail}
              sx={inputSx} />
          </Box>

          {/* Address Autofill */}
          <Box sx={{ mb: 2.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75 }}>Address</Typography>
            <Autocomplete
              freeSolo
              options={addressOptions}
              getOptionLabel={(option) => typeof option === "string" ? option : option.display_name}
              filterOptions={x => x}
              loading={addressLoading}
              inputValue={addressInput}
              onInputChange={(_, val) => setAddressInput(val)}
              onChange={(_, val) => {
                if (val && typeof val !== "string") {
                  const addr = val.address;
                  formik.setFieldValue("customerStreetNumber", addr.house_number || "");
                  formik.setFieldValue("customerStreetName", addr.road || "");
                  formik.setFieldValue("customerCity", addr.city || addr.town || addr.village || "");
                  formik.setFieldValue("customerProvince", addr.state || addr.province || "");
                  formik.setFieldValue("customerPostalCode", addr.postcode || "");
                  setAddressInput(val.display_name);
                }
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} sx={{ display: "flex", alignItems: "flex-start", gap: 1, py: 1 }}>
                  <LocationOnIcon sx={{ fontSize: 16, color: "#94a3b8", mt: 0.25, flexShrink: 0 }} />
                  <Typography fontSize={12} color="#334155">{option.display_name}</Typography>
                </Box>
              )}
              renderInput={(params) => (
                <TextField {...params} size="small" placeholder="Start typing an address..."
                  InputProps={{ ...params.InputProps, endAdornment: (
                    <>
                      {addressLoading && <CircularProgress size={16} sx={{ color: "#94a3b8" }} />}
                      {params.InputProps.endAdornment}
                    </>
                  )}}
                  sx={inputSx} />
              )}
            />
            {formik.values.customerCity && (
              <Box sx={{ mt: 1, px: 1.5, py: 1, bgcolor: "#f8fafc", borderRadius: 1.5, border: "1px solid #f1f5f9" }}>
                <Typography fontSize={11} color="#64748b">
                  {[
                    [formik.values.customerStreetNumber, formik.values.customerStreetName].filter(Boolean).join(" "),
                    [formik.values.customerCity, formik.values.customerProvince, formik.values.customerPostalCode].filter(Boolean).join(", "),
                  ].filter(Boolean).join(", ")}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Logo — compact */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 0.5 }}>
            {selectedImage ? (
              <Box sx={{ width: 44, height: 44, borderRadius: 2, overflow: "hidden", border: "1px solid #e2e8f0", position: "relative", flexShrink: 0 }}>
                <img src={URL.createObjectURL(selectedImage)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                <IconButton size="small" onClick={() => setSelectedImage(undefined)}
                  sx={{ position: "absolute", top: -4, right: -4, bgcolor: "#fff", width: 18, height: 18, border: "1px solid #e2e8f0", "&:hover": { bgcolor: "#fef2f2" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 10, color: "#ef4444" }} />
                </IconButton>
              </Box>
            ) : (
              <>
                <input accept="image/*" style={{ display: "none" }} id="quick-supplier-logo" type="file" onChange={handleImageChange} />
                <label htmlFor="quick-supplier-logo">
                  <Button component="span" size="small" variant="text"
                    startIcon={<CloudUploadIcon sx={{ fontSize: 14 }} />}
                    sx={{ textTransform: "none", fontSize: 11, fontWeight: 600, color: "#94a3b8", "&:hover": { color: "#64748b" } }}>
                    Add logo (optional)
                  </Button>
                </label>
              </>
            )}
          </Box>
        </Box>

        {/* Actions */}
        <Box sx={{ px: 3, py: 2, bgcolor: "#fafbfc", borderTop: "1px solid #f1f5f9", display: "flex", gap: 1.5, justifyContent: "flex-end" }}>
          <Button variant="outlined" size="small" onClick={() => handleCloseDialog(false)}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 600, borderColor: "#e2e8f0", color: "#64748b", px: 2.5 }}>
            Cancel
          </Button>
          <Button variant="contained" disableElevation size="small" type="submit" disabled={!formValid || isLoading}
            startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <SaveIcon sx={{ fontSize: 16 }} />}
            sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, fontWeight: 700, bgcolor: "#0f172a", px: 2.5,
              "&:hover": { bgcolor: "#1e293b" }, "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" } }}>
            {isLoading ? "Creating..." : "Create Supplier"}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default QuickAddSupplier;
